// trackingReplaysStore.ts
import { create } from "zustand";
import type { CustomAlertProps } from "../../components/base/CustomAlert";
import { getError } from "../../utils/getError";
import type { TFunction } from "i18next";
import type { UseFormReset } from "react-hook-form";
import api from "../../utils/api";

export interface TrackPoint {
  lat: number | string;
  long: number | string;
  event?: Record<string, any>;
  date?: string;
}

export interface TrackingReplay {
  id: string;
  name: string;
  created_by: string;
  company_id: string;
  vehicle_id: string;
  device_id: string;
  start_date: string;
  end_date: string;
  positions: TrackPoint[];
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

interface TrackingReplaysStore {
  trackingReplays: TrackingReplay[];
  loading: boolean;
  error: string | null;
  currentReplay: TrackingReplay | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchTrackingReplaysList: (params?: {
    limit?: number;
    page?: number;
    company_id?: string;
    vehicle_id?: string;
  }) => Promise<void>;
  getTrackingReplayById: (id: string) => Promise<TrackingReplay | null>;
  addTrackingReplayToStore: (replay: TrackingReplay) => void;
  updateTrackingReplayInStore: (id: string, updatedReplay: Partial<TrackingReplay>) => void;
  removeTrackingReplayFromStore: (id: string) => void;
  setCurrentReplay: (replay: TrackingReplay | null) => void;
  clearTrackingReplays: () => void;
}

export const useTrackingReplaysStore = create<TrackingReplaysStore>((set, get) => ({
  trackingReplays: [],
  loading: false,
  error: null,
  currentReplay: null,
  meta: undefined,

  fetchTrackingReplaysList: async (params = {}) => {
    if (get().loading) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const queryParams: Record<string, any> = {
        limit: params.limit ?? 20,
        page: params.page ?? 1,
      };

      if (params.company_id) {
        queryParams.company_id = params.company_id;
      }
      if (params.vehicle_id) {
        queryParams.vehicle_id = params.vehicle_id;
      }

      const { data } = await api.get("/trackingReplaysList", { params: queryParams });

      set({
        trackingReplays: data.data || [],
        meta: data.meta,
        loading: false,
      });
    } catch (err: any) {
      set({
        error: getError(err),
        loading: false,
      });
    }
  },

  getTrackingReplayById: async (id: string) => {
    try {
      const { data } = await api.get("/trackingReplays", { params: { id } });
      return data.data;
    } catch (err: any) {
      console.error("Error fetching tracking replay by ID:", err);
      return null;
    }
  },

  addTrackingReplayToStore: (replay: TrackingReplay) => {
    set((state) => ({
      trackingReplays: [replay, ...state.trackingReplays],
      meta: state.meta
        ? {
            ...state.meta,
            count: state.meta.count + 1,
            totalItems: state.meta.totalItems + 1,
          }
        : undefined,
    }));
  },

  updateTrackingReplayInStore: (id: string, updatedReplay: Partial<TrackingReplay>) => {
    set((state) => ({
      trackingReplays: state.trackingReplays.map((replay) =>
        replay.id === id ? { ...replay, ...updatedReplay } : replay
      ),
      currentReplay: state.currentReplay?.id === id 
        ? { ...state.currentReplay, ...updatedReplay }
        : state.currentReplay,
    }));
  },

  removeTrackingReplayFromStore: (id: string) => {
    set((state) => ({
      trackingReplays: state.trackingReplays.filter((replay) => replay.id !== id),
      currentReplay: state.currentReplay?.id === id ? null : state.currentReplay,
      meta: state.meta
        ? {
            ...state.meta,
            count: state.meta.count - 1,
            totalItems: state.meta.totalItems - 1,
          }
        : undefined,
    }));
  },

  setCurrentReplay: (replay: TrackingReplay | null) => {
    set({ currentReplay: replay });
  },

  clearTrackingReplays: () => {
    set({ trackingReplays: [], currentReplay: null, meta: undefined });
  },
}));

// Funciones CRUD
export const createTrackingReplay = async (
  replayData: Omit<TrackingReplay, 'id'>,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    const { data } = await api.post("/trackingReplays", replayData);

    if (data.data) {
      useTrackingReplaysStore.getState().addTrackingReplayToStore(data.data);
    }

    setLoadingSubmit(false);
    showAlert({
      title: t("success"),
      text: t("trackingReplayCreated"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const updateTrackingReplay = async (
  replayId: string,
  replayData: Partial<TrackingReplay>,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    const { data } = await api.put("/trackingReplays", {
      id: replayId,
      ...replayData
    });

    if (data.data) {
      useTrackingReplaysStore.getState().updateTrackingReplayInStore(replayId, data.data);
    }

    setLoadingSubmit(false);
    showAlert({
      title: t("success"),
      text: t("trackingReplayUpdated"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const deleteTrackingReplay = async (
  replayId: string,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"adminSections", undefined>,
  onClose: () => void
) => {
  try {
    await api.delete("/trackingReplays", { params: { id: replayId } });

    useTrackingReplaysStore.getState().removeTrackingReplayFromStore(replayId);

    setLoadingSubmit(false);
    showAlert({
      title: t("success", "Éxito"),
      text: t("trackingReplayDeleted", "Grabación eliminada correctamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
      },
      showCancelButton: false,
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};